import { memo, useMemo } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { resolveAssistantHref } from "../../lib/assistant-links";

interface AssistantMarkdownProps {
  text: string;
  onInternalNavigate?: () => void;
}

const remarkPlugins = [remarkGfm];

function AssistantMarkdown({ text, onInternalNavigate }: AssistantMarkdownProps) {
  const components = useMemo<Components>(
    () => ({
      a: ({ href, children, node: _node, ...rest }) => {
        const resolved = resolveAssistantHref(
          href,
          typeof window === "undefined" ? undefined : window.location.origin,
        );
        if (resolved.kind === "internal") {
          return (
            <Link
              to={resolved.to}
              onClick={onInternalNavigate}
              className="font-medium text-blue-600 dark:text-blue-400 underline underline-offset-2 decoration-blue-600/40 dark:decoration-blue-400/40 hover:decoration-current"
            >
              {children}
            </Link>
          );
        }
        if (resolved.kind === "external") {
          return (
            <a
              {...rest}
              href={resolved.href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-baseline gap-0.5 font-medium text-blue-600 dark:text-blue-400 underline underline-offset-2 decoration-blue-600/40 dark:decoration-blue-400/40 hover:decoration-current"
            >
              {children}
              <ExternalLink aria-hidden className="h-3 w-3 self-center" />
            </a>
          );
        }
        return <span>{children}</span>;
      },
      p: ({ node: _node, className, ...rest }) => (
        <p className={cn("my-2 first:mt-0 last:mb-0 leading-relaxed", className)} dir="auto" {...rest} />
      ),
      h1: ({ node: _node, className, ...rest }) => (
        <h3 className={cn("mt-4 mb-2 first:mt-0 text-base font-semibold", className)} dir="auto" {...rest} />
      ),
      h2: ({ node: _node, className, ...rest }) => (
        <h3 className={cn("mt-4 mb-2 first:mt-0 text-[15px] font-semibold", className)} dir="auto" {...rest} />
      ),
      h3: ({ node: _node, className, ...rest }) => (
        <h4 className={cn("mt-3 mb-1.5 first:mt-0 text-sm font-semibold", className)} dir="auto" {...rest} />
      ),
      h4: ({ node: _node, className, ...rest }) => (
        <h5 className={cn("mt-3 mb-1 first:mt-0 text-sm font-semibold", className)} dir="auto" {...rest} />
      ),
      ul: ({ node: _node, className, ...rest }) => (
        <ul className={cn("my-2 ms-5 list-disc space-y-1 marker:text-muted-foreground", className)} dir="auto" {...rest} />
      ),
      ol: ({ node: _node, className, ...rest }) => (
        <ol className={cn("my-2 ms-5 list-decimal space-y-1 marker:text-muted-foreground", className)} dir="auto" {...rest} />
      ),
      li: ({ node: _node, className, ...rest }) => (
        <li className={cn("leading-relaxed ps-1 [&>p]:my-1", className)} {...rest} />
      ),
      blockquote: ({ node: _node, className, ...rest }) => (
        <blockquote
          className={cn("my-2 border-s-2 border-border ps-3 text-muted-foreground", className)}
          dir="auto"
          {...rest}
        />
      ),
      hr: ({ node: _node, className, ...rest }) => (
        <hr className={cn("my-4 border-border", className)} {...rest} />
      ),
      strong: ({ node: _node, className, ...rest }) => (
        <strong className={cn("font-semibold text-foreground", className)} {...rest} />
      ),
      pre: ({ node: _node, className, ...rest }) => (
        <pre
          className={cn(
            "my-3 overflow-x-auto rounded-md border border-border bg-muted/60 p-3 text-xs leading-relaxed font-mono [&>code]:bg-transparent [&>code]:p-0 [&>code]:border-0 [&>code]:text-xs",
            className,
          )}
          dir="ltr"
          {...rest}
        />
      ),
      code: ({ node: _node, className, ...rest }) => (
        <code
          className={cn(
            "rounded border border-border bg-muted px-1 py-0.5 font-mono text-[0.85em]",
            className,
          )}
          {...rest}
        />
      ),
      table: ({ node: _node, className, ...rest }) => (
        <div className="my-3 w-full overflow-x-auto rounded-md border border-border">
          <table className={cn("w-full border-collapse text-xs", className)} {...rest} />
        </div>
      ),
      thead: ({ node: _node, className, ...rest }) => (
        <thead className={cn("bg-muted/60", className)} {...rest} />
      ),
      tr: ({ node: _node, className, ...rest }) => (
        <tr className={cn("border-b border-border last:border-b-0", className)} {...rest} />
      ),
      th: ({ node: _node, className, ...rest }) => (
        <th
          className={cn(
            "px-2.5 py-1.5 text-start font-medium text-muted-foreground whitespace-nowrap",
            className,
          )}
          dir="auto"
          {...rest}
        />
      ),
      td: ({ node: _node, className, ...rest }) => (
        <td className={cn("px-2.5 py-1.5 align-top", className)} dir="auto" {...rest} />
      ),
      img: ({ node: _node, alt }) => <span className="text-muted-foreground">{alt || ""}</span>,
    }),
    [onInternalNavigate],
  );

  return (
    <div className="text-sm text-foreground break-words">
      <Markdown remarkPlugins={remarkPlugins} components={components}>
        {text}
      </Markdown>
    </div>
  );
}

export default memo(AssistantMarkdown);
